import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { global } from "@shared/libs/Global";
import { getInitPopUp } from "@shared/utils/CommonUtils";
import { fnGetDataSet } from "@shared/api/restApi";
import { showDialog } from "@widgets/CommonDialog";
import SearchPanel from "@widgets/SearchPanel";
import SnSearchBtn from "@shared/components/SnSearchBtn";
import SnGrid from "@shared/components/GridV2/SnGrid";

interface popupProps {
  selectedData?: any;
  modalRef?: any;
}

const SN_SCT_SIO_REG_002_POP_001 = forwardRef(
  ({ selectedData, modalRef }: popupProps, ref: React.Ref<any>) => {
    const { userInfo } = global();

    const searchRef = useRef<any>(null);
    const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
      grid1: React.createRef(),
      grid2: React.createRef(),
    });

    const [formData, setFormData] = useState<any>();
    const [formControlData, setFormControlData] = useState<any[]>([]);
    const [columns, setColums] = useState<any>({
      grid1: [],
      grid2: [],
    });
    const [dataSources, setDataSources] = useState<any>({
      grid1: [],
      grid2: [],
    });

    const [popupParent, setPopupParent] = useState<any>(null);

    useImperativeHandle(ref, () => ({
      checkValidity: async () => {
        // 외주처(cd_cust), 외주형태(cd_sct), 거래구분(fg_trns), 환종(cd_exch), 환율(rt_exch)
        let selectedHeader: any[] =
          gridRefs.current["grid1"].current?.getCheckedRows();
        if (selectedHeader.length === 0) return true;

        const compareKeys = [
          "cd_cust", // 외주처
          "cd_sct", // 외주형태
          "fg_trns", // 거래구분
          "cd_exch", // 환종
          "rt_exch", // 환율
        ] as const;

        const baseRow = selectedHeader[0];
        const isValid = compareKeys.every((key) =>
          selectedHeader.every((row: any) => row[key] === baseRow[key])
        );
        if (!isValid) {
          await showDialog(
            "alter",
            "외주처, 외주형태, 거래구분, 환종, 환율이 동일한 건만 적용 가능합니다."
          );
          return false;
        }
        return true;
      },
      getOkayData: () => {
        return {
          rtnData: [
            gridRefs.current["grid1"].current?.getCheckedRows() || [],
            gridRefs.current["grid2"].current?.getCheckedRows() || [],
          ],
        };
      },
    }));

    useEffect(() => {
      if (modalRef?.current) {
        setPopupParent(modalRef.current.getPopupParent());
      }
    }, [modalRef?.current]);

    useEffect(() => {
      const fetchform = async () => {
        try {
          const rtn: any = await getInitPopUp("SN_SCT_SIO_REG_002_POP_001", {});
          const mergedData: any = { ...rtn.formData, ...selectedData };
          setFormControlData(rtn.formControlData);
          setFormData(mergedData);
          setColums({
            grid1: rtn.grid.grid1,
            grid2: rtn.grid.grid2,
          });
          gridRefs.current["grid1"].current.selectAndFocusRow(0);
        } catch (error) {}
      };
      fetchform();
    }, []);

    const onSearch = async () => {
      try {
        const param = {
          id: "sn_sct_sio_reg_002_pop_001_01",
          params: searchRef.current.getFormdata(),
        };

        const data: any = await fnGetDataSet(param);
        setDataSources({
          grid1: data[0] || [],
          grid2: data[1] || [],
        });
        gridRefs.current["grid1"].current.resetUpdate();
        gridRefs.current["grid2"].current.resetUpdate();
      } catch (error: any) {}
    };

    const onFocusedRowChanged1 = (row: any) => {
      const no_po = row?.no_po;
      gridRefs.current["grid2"].current.applyFilters({ no_po: no_po });
    };

    const onCheckedRowChaged1 = (
      value: boolean,
      type: "row" | "header" = "row"
    ) => {
      if (type === "header") {
        gridRefs.current["grid2"].current.setCheckedAll(value);
      } else {
        gridRefs.current["grid2"].current.setCheckedAllWithFilter(value);
      }
    };

    const onCheckedRowChaged2 = (value: boolean) => {
      const checkdRows =
        gridRefs.current["grid2"].current.getCheckedFilteredRows();
      if (checkdRows.length > 0)
        gridRefs.current["grid1"].current.setCheckedFocusedRow(true, "noEvent");
      else
        gridRefs.current["grid1"].current.setCheckedFocusedRow(
          false,
          "noEvent"
        );
    };

    const grids = useMemo(
      () => ({
        grid1: {
          columns: columns.grid1,
          dataSource: dataSources.grid1,
          events: {
            onFocusedRowChanged: onFocusedRowChanged1,
            onCheckedRowChaged: onCheckedRowChaged1,
          },
        },
        grid2: {
          columns: columns.grid2,
          dataSource: dataSources.grid2,
          events: {
            onCheckedRowChaged: onCheckedRowChaged2,
          },
        },
      }),
      [columns, dataSources]
    );

    return (
      <div className="h-full flex flex-col">
        <div>
          <SearchPanel
            ref={searchRef}
            data={formData}
            controls={formControlData}
            popupRef={ref}
          ></SearchPanel>
        </div>
        <div className="mb-2" style={{ textAlign: "center" }}>
          <SnSearchBtn
            text={"조회"}
            onClick={onSearch}
            sx={{ width: 80, marginTop: 1.5 }}
            spinner={true}
          />
        </div>

        <div className="flex-1 flex flex-col gap-2 min-h-00">
          <div className="flex-1 min-h-0">
            <SnGrid
              ref={gridRefs.current["grid1"]}
              columnConfig={grids.grid1.columns}
              rowData={grids.grid1.dataSource}
              etcProps={{
                rowSelection: "multiple",
                ischeckboxSelection: true,
                popupParent: popupParent,
              }}
              events={grids.grid1.events}
            />
          </div>
          <div className="flex-1 min-h-10">
            <SnGrid
              ref={gridRefs.current["grid2"]}
              columnConfig={grids.grid2.columns}
              rowData={grids.grid2.dataSource}
              etcProps={{
                rowSelection: "multiple",
                ischeckboxSelection: true,
                popupParent: popupParent,
              }}
              events={grids.grid2.events}
            />
          </div>
        </div>
      </div>
    );
  }
);

export default SN_SCT_SIO_REG_002_POP_001;
